import { useCallback, useRef, useState } from "react";
import { ChevronsLeftRight } from "lucide-react";

export const BeforeAfterSlider = ({ before, after, title }) => {
  const [pos, setPos] = useState(50);
  const ref = useRef(null);
  const dragging = useRef(false);

  const move = useCallback((clientX) => {
    const el = ref.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    setPos(Math.min(100, Math.max(0, ((clientX - r.left) / r.width) * 100)));
  }, []);

  const onPointerDown = (e) => {
    dragging.current = true;
    move(e.clientX);
    const onMove = (ev) => dragging.current && move(ev.clientX);
    const onUp = () => {
      dragging.current = false;
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
    };
    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
  };

  return (
    <div
      ref={ref}
      onPointerDown={onPointerDown}
      data-testid="before-after-slider"
      role="slider"
      aria-label={`Before and after comparison for ${title}`}
      aria-valuenow={Math.round(pos)}
      aria-valuemin={0}
      aria-valuemax={100}
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === "ArrowLeft") setPos((p) => Math.max(0, p - 5));
        if (e.key === "ArrowRight") setPos((p) => Math.min(100, p + 5));
      }}
      className="group relative aspect-[4/3] w-full cursor-ew-resize touch-none select-none overflow-hidden rounded-[2rem] focus:outline-none focus:ring-2 focus:ring-[#D4AF37]"
    >
      <img src={after} alt={`${title} — after treatment`} draggable="false" className="absolute inset-0 h-full w-full object-cover" />
      <div className="absolute inset-0" style={{ clipPath: `inset(0 ${100 - pos}% 0 0)` }}>
        <img src={before} alt={`${title} — before treatment`} draggable="false" className="absolute inset-0 h-full w-full object-cover" />
      </div>
      <span className="glass absolute left-4 top-4 rounded-full px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.2em] text-[#1A1615]">Before</span>
      <span className="glass absolute right-4 top-4 rounded-full px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.2em] text-[#1A1615]">After</span>
      <div className="absolute inset-y-0" style={{ left: `${pos}%` }}>
        <div className="absolute inset-y-0 -ml-px w-0.5 bg-[#D4AF37] shadow-[0_0_12px_rgba(212,175,55,0.8)]" />
        <div className="absolute top-1/2 flex h-12 w-12 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full bg-gradient-to-r from-[#D4AF37] to-[#AA7C11] text-[#1A1615] shadow-[0_8px_25px_rgba(212,175,55,0.5)] transition-transform duration-300 group-hover:scale-110">
          <ChevronsLeftRight className="h-5 w-5" aria-hidden="true" />
        </div>
      </div>
    </div>
  );
};
